import { useState, useRef, useEffect, useCallback } from "react"
import { FREE_COLORS, PRO_COLORS, canUseCustomColors, isValidColor } from "../lib/colors"
import type { Plan } from "../lib/plans"

function hexToRgb(hex: string) {
  const n = hex.replace("#", "")
  return {
    r: parseInt(n.slice(0, 2), 16) / 255,
    g: parseInt(n.slice(2, 4), 16) / 255,
    b: parseInt(n.slice(4, 6), 16) / 255,
  }
}

function rgbToHex(r: number, g: number, b: number) {
  const toHex = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255))).toString(16).padStart(2, "0")
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toLowerCase()
}

function hexToHsv(hex: string) {
  const { r, g, b } = hexToRgb(hex)
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const d = max - min
  const v = max
  const s = max === 0 ? 0 : d / max
  let h = 0
  if (d !== 0) {
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
    else if (max === g) h = ((b - r) / d + 2) / 6
    else h = ((r - g) / d + 4) / 6
  }
  return { h: h * 360, s: s * 100, v: v * 100 }
}

function hsvToHex(h: number, s: number, v: number) {
  const hh = ((h % 360) + 360) % 360
  const ss = Math.max(0, Math.min(100, s)) / 100
  const vv = Math.max(0, Math.min(100, v)) / 100
  const c = vv * ss
  const x = c * (1 - Math.abs(((hh / 60) % 2) - 1))
  const m = vv - c
  let r = 0, g = 0, b = 0
  if (hh < 60) { r = c; g = x }
  else if (hh < 120) { r = x; g = c }
  else if (hh < 180) { g = c; b = x }
  else if (hh < 240) { g = x; b = c }
  else if (hh < 300) { r = x; b = c }
  else { r = c; b = x }
  return rgbToHex(r + m, g + m, b + m)
}

function SaturationValueBox({
  h,
  s,
  v,
  onChange,
}: {
  h: number
  s: number
  v: number
  onChange: (s: number, v: number) => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const dragging = useRef(false)

  const update = useCallback((clientX: number, clientY: number) => {
    const rect = ref.current?.getBoundingClientRect()
    if (!rect) return
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height))
    onChange(x * 100, (1 - y) * 100)
  }, [onChange])

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!dragging.current) return
      e.preventDefault()
      update(e.clientX, e.clientY)
    }
    const handleUp = () => {
      dragging.current = false
    }
    window.addEventListener("mousemove", handleMove)
    window.addEventListener("mouseup", handleUp)
    window.addEventListener("touchmove", handleMove as any, { passive: false })
    window.addEventListener("touchend", handleUp)
    return () => {
      window.removeEventListener("mousemove", handleMove)
      window.removeEventListener("mouseup", handleUp)
      window.removeEventListener("touchmove", handleMove as any)
      window.removeEventListener("touchend", handleUp)
    }
  }, [update])

  return (
    <div
      ref={ref}
      className="relative h-24 w-full cursor-crosshair rounded-md border border-border"
      style={{ backgroundColor: hsvToHex(h, 100, 100) }}
      onMouseDown={(e) => { dragging.current = true; update(e.clientX, e.clientY) }}
      onTouchStart={(e) => { dragging.current = true; update(e.touches[0].clientX, e.touches[0].clientY) }}
    >
      <div className="pointer-events-none absolute inset-0 rounded-md" style={{ background: "linear-gradient(to right, #fff, transparent)" }} />
      <div className="pointer-events-none absolute inset-0 rounded-md" style={{ background: "linear-gradient(to top, #000, transparent)" }} />
      <div
        className="pointer-events-none absolute h-3 w-3 rounded-full border-2 border-white shadow-sm"
        style={{ left: `calc(${s}% - 6px)`, top: `calc(${100 - v}% - 6px)`, backgroundColor: hsvToHex(h, s, v) }}
      />
    </div>
  )
}

function HueSlider({ h, onChange }: { h: number; onChange: (h: number) => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const dragging = useRef(false)

  const update = useCallback((clientX: number) => {
    const rect = ref.current?.getBoundingClientRect()
    if (!rect) return
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    onChange(x * 360)
  }, [onChange])

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!dragging.current) return
      e.preventDefault()
      update(e.clientX)
    }
    const handleUp = () => { dragging.current = false }
    window.addEventListener("mousemove", handleMove)
    window.addEventListener("mouseup", handleUp)
    window.addEventListener("touchmove", handleMove as any, { passive: false })
    window.addEventListener("touchend", handleUp)
    return () => {
      window.removeEventListener("mousemove", handleMove)
      window.removeEventListener("mouseup", handleUp)
      window.removeEventListener("touchmove", handleMove as any)
      window.removeEventListener("touchend", handleUp)
    }
  }, [update])

  return (
    <div
      ref={ref}
      className="relative h-3 w-full cursor-pointer rounded-md border border-border"
      style={{ background: "linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)" }}
      onMouseDown={(e) => { dragging.current = true; update(e.clientX) }}
      onTouchStart={(e) => { dragging.current = true; update(e.touches[0].clientX) }}
    >
      <div
        className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-sm"
        style={{ left: `${(h / 360) * 100}%`, backgroundColor: hsvToHex(h, 100, 100) }}
      />
    </div>
  )
}

export function ColorPicker({
  plan,
  value,
  onChange,
  showCustom = true,
}: {
  plan: Plan
  value: string | null | undefined
  onChange: (color: string | null) => void
  showCustom?: boolean
}) {
  const palette = plan === "free" ? FREE_COLORS : PRO_COLORS
  const isCustom = !!value && isValidColor(value) && !(palette as readonly string[]).includes(value)
  const [custom, setCustom] = useState(isCustom ? value : "")
  const allowCustom = canUseCustomColors(plan)
  const preview = isValidColor(custom) ? custom : value || "#000000"
  const hsv = hexToHsv(preview)

  const updateHsv = (next: { h?: number; s?: number; v?: number }) => {
    const color = hsvToHex(next.h ?? hsv.h, next.s ?? hsv.s, next.v ?? hsv.v)
    setCustom(color)
    onChange(color)
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {palette.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onChange(value === c ? null : c)}
            className={`h-6 w-6 rounded-full border-2 transition-transform hover:scale-110 ${value === c ? "border-foreground ring-1 ring-foreground" : "border-transparent"}`}
            style={{ backgroundColor: c }}
            title={c}
            aria-label={`Select color ${c}`}
          />
        ))}
      </div>
      {showCustom && allowCustom && (
        <div className="space-y-2 rounded-md border border-border bg-background p-2">
          <div className="flex items-center gap-2">
            <span
              className="h-6 w-6 shrink-0 rounded-full border border-border"
              style={{ backgroundColor: preview }}
              aria-hidden="true"
            />
            <div className="flex flex-1 items-center overflow-hidden rounded-md border border-border bg-background">
              <span className="pointer-events-none select-none px-2 text-xs text-muted-foreground">#</span>
              <input
                type="text"
                value={custom.replace(/^#/, "")}
                onChange={(e) => {
                  const hex = e.target.value.replace(/[^0-9a-fA-F]/g, "").slice(0, 6)
                  const full = hex.length === 6 ? `#${hex.toLowerCase()}` : `#${hex}`
                  setCustom(full)
                  if (isValidColor(full)) onChange(full)
                }}
                onBlur={() => {
                  if (custom && !isValidColor(custom)) setCustom(isCustom ? value : "")
                }}
                placeholder="rrggbb"
                className="w-full bg-transparent px-1 py-1 text-xs text-foreground outline-none"
                aria-label="Custom hex color"
              />
            </div>
          </div>
          <SaturationValueBox h={hsv.h} s={hsv.s} v={hsv.v} onChange={(s, v) => updateHsv({ s, v })} />
          <HueSlider h={hsv.h} onChange={(h) => updateHsv({ h })} />
        </div>
      )}
      {!allowCustom && showCustom && (
        <p className="text-xs text-muted-foreground">Upgrade to Pro for the full color palette, custom colors, and gradient backgrounds.</p>
      )}
    </div>
  )
}
